import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check, Loader2, Package, Ruler, Sparkles, ShoppingBag, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, whatsappLink } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";

export default function ProductDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    setLoading(true);
    setActiveImg(0);
    api
      .get(`/products/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ product: null, related: [] }))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center text-[#1B3B36]">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }

  const p = data?.product;
  if (!p) {
    return (
      <div className="max-w-3xl mx-auto text-center py-32 px-6">
        <div className="overline">404</div>
        <h1 className="mt-4 font-serif text-5xl text-[#1B3B36]">This piece isn't here.</h1>
        <p className="mt-4 text-[#4A4A4A] font-light">It may have been retired to the archive.</p>
        <Link
          to="/collections"
          className="mt-8 inline-flex items-center gap-3 bg-[#1B3B36] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26]"
        >
          Back to Collection
        </Link>
      </div>
    );
  }

  const discount = p.mrp && p.mrp > p.price_from ? Math.round((1 - p.price_from / p.mrp) * 100) : 0;
  const wa = whatsappLink(
    `Hello Iryaa Homes, I'd like to enquire about "${p.name}" (₹${p.price_from.toLocaleString("en-IN")}).`
  );

  return (
    <div data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pt-8 md:pt-10 text-[12px] uppercase tracking-[0.18em] text-[#84968C]">
        <Link to="/" className="link-underline">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/collections" className="link-underline">Collections</Link>
        <span className="mx-2">/</span>
        <Link to={`/collections?cat=${p.category}`} className="link-underline">
          {p.category.replace("-", " ")}
        </Link>
      </div>

      {/* Hero: gallery + details */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-10 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="relative aspect-[4/5] overflow-hidden bg-[#EFECE5]">
            <img
              src={p.gallery[activeImg]}
              alt={p.name}
              data-testid="product-detail-main-image"
              className="w-full h-full object-cover"
            />
            {discount > 0 && (
              <div className="absolute top-5 left-5 overline text-[10px] bg-[#1B3B36] text-[#F9F8F6] px-3 py-1">
                {discount}% off
              </div>
            )}
          </div>
          <div className="mt-4 grid grid-cols-4 gap-3">
            {p.gallery.map((g, i) => (
              <button
                key={i}
                data-testid={`product-detail-thumb-${i}`}
                onClick={() => setActiveImg(i)}
                className={`aspect-square overflow-hidden border transition-colors ${
                  activeImg === i ? "border-[#1B3B36]" : "border-[#D8D4CC]"
                }`}
              >
                <img src={g} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-5">
          <div className="overline">{p.category.replace("-", " ")} · {p.material.replace("_", " ")}</div>
          <h1 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl font-light text-[#1B3B36] leading-[1.05] tracking-tight">
            {p.name}
          </h1>
          <p className="mt-6 text-[15px] leading-relaxed text-[#4A4A4A] font-light">
            {p.story}
          </p>

          <div className="mt-8 flex items-baseline gap-4">
            <div className="font-serif text-4xl text-[#1B3B36]">
              ₹{p.price_from.toLocaleString("en-IN")}
            </div>
            {p.mrp && (
              <div className="text-[15px] text-[#84968C] line-through">
                ₹{p.mrp.toLocaleString("en-IN")}
              </div>
            )}
            {discount > 0 && (
              <div className="text-[12px] uppercase tracking-[0.18em] text-[#1B3B36]">
                You save ₹{(p.mrp - p.price_from).toLocaleString("en-IN")}
              </div>
            )}
          </div>
          <div className="mt-2 text-[12px] uppercase tracking-[0.18em] text-[#84968C]">
            {p.lead_time}
          </div>

          <div className="mt-8 flex flex-col gap-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center border border-[#1B3B36]" data-testid="product-detail-qty">
                <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="product-detail-qty-minus" className="w-11 h-14 flex items-center justify-center text-[#1B3B36] hover:bg-[#EFECE5]"><Minus size={14} /></button>
                <div className="w-10 text-center text-[15px]">{qty}</div>
                <button onClick={() => setQty(qty + 1)} data-testid="product-detail-qty-plus" className="w-11 h-14 flex items-center justify-center text-[#1B3B36] hover:bg-[#EFECE5]"><Plus size={14} /></button>
              </div>
              <button
                onClick={() => {
                  add(p, qty);
                  toast.success(`${p.name} × ${qty} added to basket`);
                }}
                data-testid="product-detail-add-to-cart"
                className="flex-1 inline-flex items-center justify-center gap-3 bg-[#1B3B36] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26]"
              >
                <ShoppingBag size={14} /> Add to basket
              </button>
            </div>
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              data-testid="product-detail-whatsapp"
              className="inline-flex items-center justify-center gap-3 border border-[#1B3B36] text-[#1B3B36] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#1B3B36] hover:text-[#F9F8F6]"
            >
              Enquire on WhatsApp
            </a>
          </div>

          {/* Meta strips */}
          <div className="mt-10 grid grid-cols-2 gap-6 border-t border-[#D8D4CC] pt-6">
            <div>
              <div className="flex items-center gap-2 text-[#84968C]">
                <Ruler size={14} />
                <span className="overline">Dimensions</span>
              </div>
              <div className="mt-2 font-serif text-lg text-[#1B3B36]">{p.dimensions}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-[#84968C]">
                <Package size={14} />
                <span className="overline">Material</span>
              </div>
              <div className="mt-2 font-serif text-lg text-[#1B3B36] capitalize">
                {p.material.replace("_", " ")}
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="mt-8">
            <div className="overline">Features</div>
            <ul className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-[13px] text-[#4A4A4A]">
                  <Check size={14} className="mt-1 text-[#1B3B36] flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Care */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-20 border-t border-[#D8D4CC] grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="overline">Care & keeping</div>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl font-light text-[#1B3B36] leading-tight">
            A handmade piece rewards patience.
          </h2>
        </div>
        <ol className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {p.care.map((c, i) => (
            <li key={i} className="border-t border-[#1B3B36] pt-4">
              <div className="font-serif text-xl text-[#1B3B36]">0{i + 1}</div>
              <p className="mt-2 text-[14px] text-[#4A4A4A] font-light leading-relaxed">{c}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Custom CTA */}
      <section className="bg-[#EFECE5]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="overline">Want this — but in your colours?</div>
            <h3 className="mt-3 font-serif text-3xl md:text-4xl text-[#1B3B36] leading-tight">
              Commission a bespoke version in our Custom Studio.
            </h3>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/designer"
              data-testid="product-detail-custom-cta"
              className="inline-flex items-center gap-3 bg-[#1B3B36] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26]"
            >
              <Sparkles size={14} /> Design yours <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Related */}
      {data.related?.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
          <div className="overline">You might also like</div>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl font-light text-[#1B3B36]">
            More from the same atelier bench.
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.related.map((r) => (
              <ProductCard key={r.id} product={r} testIdPrefix="related" />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
